import React, { useState } from "react";
import { Controller } from "react-hook-form";
import { courseTitles } from "../dropdownsData";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const CourseLine = ({
  id,
  onRemove,
  control,
  errors,
  trigger,
  selectedCourses,
  handleCourseChange,
  getValues,
}) => {
  const [startDate, setStartDate] = useState(null);

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleRemove = () => {
    const courseTitle = getValues(`course${id}CourseTitle`);
    handleCourseChange(id, courseTitle, true);
    onRemove(id);
  };

  return (
    <div className="flex flex-col md:flex-row mb-1 md:items-center">
      <div className="flex flex-col md:flex-row items-center">
        <Controller
          render={({ field }) => (
            <FormControl
              fullWidth
              error={Boolean(errors[`course${id}CourseTitle`])}
            >
              <InputLabel id={`course-type-label-${id}`}>
                Course <span className="text-orange">*</span>
              </InputLabel>
              <Select
                labelId={`course-type-label-${id}`}
                id={`course-type-${id}`}
                label={
                  <div>
                    Course <span className="text-orange">*</span>
                  </div>
                }
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  handleCourseChange(id, e.target.value, false);
                }}
              >
                {courseTitles.map((courseTitle, index) => (
                  <MenuItem
                    key={index}
                    value={courseTitle}
                    disabled={selectedCourses.some(
                      (c) => c.courseTitle === courseTitle
                    )}
                  >
                    {courseTitle}
                  </MenuItem>
                ))}
              </Select>
              {errors[`course${id}CourseTitle`] && (
                <FormHelperText>
                  {errors[`course${id}CourseTitle`].message}
                </FormHelperText>
              )}
            </FormControl>
          )}
          name={`course${id}CourseTitle`}
          control={control}
          rules={{
            required: "Title is required",
          }}
        />
        <div className="md:ml-1 mt-1 md:mt-0 w-full">
          <Controller
            render={({ field }) => (
              <TextField
                {...field}
                error={Boolean(errors[`course${id}Participants`])}
                helperText={
                  errors[`course${id}Participants`]
                    ? errors[`course${id}Participants`].message
                    : null
                }
                label={
                  <div>
                    Participants <span className="text-orange">*</span>
                  </div>
                }
                className="w-full"
                type="number"
                inputProps={{ min: "0", step: "1" }}
              />
            )}
            name={`course${id}Participants`}
            control={control}
            rules={{
              required: "Amount is required",
            }}
          />
        </div>
        <div className="md:ml-1 mt-1 md:mt-0 w-full md:w-11">
          <Controller
            name={`course${id}DateStart`}
            control={control}
            rules={{
              required: "Start Date is required",
            }}
            render={({ field }) => (
              <FormControl
                fullWidth
                error={Boolean(errors[`course${id}DateStart`])}
              >
                <DatePicker
                  {...field}
                  margin="normal"
                  label={
                    <div>
                      Start Date<span className="text-orange ml-0-25">*</span>
                    </div>
                  }
                  value={startDate}
                  onChange={(date) => {
                    field.onChange(date);
                    handleStartDateChange(date);
                  }}
                  disablePast
                  className="w-full md:w-11"
                  id={`date-picker-course-${id}-date-start`}
                />
                {errors[`course${id}DateStart`] && (
                  <FormHelperText error>
                    {errors[`course${id}DateStart`].message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </div>
        <div className="md:ml-1 mt-1 md:mt-0 w-full md:w-11">
          <Controller
            name={`course${id}DateEnd`}
            control={control}
            rules={{
              required: "End Date is required",
            }}
            render={({ field }) => (
              <FormControl
                fullWidth
                error={Boolean(errors[`course${id}DateEnd`])}
              >
                <DatePicker
                  {...field}
                  margin="normal"
                  label={
                    <div>
                      End Date<span className="text-orange ml-0-25">*</span>
                    </div>
                  }
                  minDate={startDate}
                  disablePast
                  className="w-full md:w-11"
                  id={`date-picker-course-${id}-date-end`}
                />
                {errors[`course${id}DateEnd`] && (
                  <FormHelperText error>
                    {errors[`course${id}DateEnd`].message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </div>

        <div className="md:ml-1 mt-1 md:mt-0 w-full">
          <Controller
            render={({ field }) => (
              <TextField
                {...field}
                label={<div>Teacher Name</div>}
                className="w-full"
              />
            )}
            name={`course${id}TeacherName`}
            control={control}
          />
        </div>
        <div className="md:ml-1 mt-1 md:mt-0 w-full">
          <Controller
            render={({ field }) => (
              <TextField
                {...field}
                error={Boolean(errors[`course${id}TeacherEmail`])}
                helperText={
                  errors[`course${id}TeacherEmail`]
                    ? errors[`course${id}TeacherEmail`].message
                    : null
                }
                label={<div>Teacher Email</div>}
                className="w-full"
                onBlur={async () => {
                  await trigger(`course${id}TeacherEmail`); // Trigger validation on blur
                  field.onBlur(); // Ensure onBlur event is propagated
                }}
              />
            )}
            name={`course${id}TeacherEmail`}
            control={control}
            rules={{
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: "Invalid email format",
              },
            }}
          />
        </div>
        <div className="md:ml-1 mt-1 md:mt-0 w-full">
          <Controller
            render={({ field }) => (
              <TextField
                {...field}
                label={<div>Teacher Code</div>}
                className="w-full"
              />
            )}
            name={`course${id}TeacherCode`}
            control={control}
          />
        </div>
      </div>

      <button
        type="button"
        className={`${
          id === 1 ? "semi-hidden" : ""
        } remove-line-button mt-1 md:mt-0 md:ml-1 mr-0-5`}
        onClick={handleRemove}
      >
        X
      </button>
    </div>
  );
};

export default CourseLine;
